import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  GraduationCap, UserPlus, Copy, CheckCircle2, Clock, XCircle, FileText,
  Mail, User, BookOpen, Loader2, Trash2, ClipboardList, ChevronRight, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatLocalDate } from '../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Awaiting Teacher' },
  active: { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Active' },
  revoked: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Revoked' },
};

const RATING_LABELS = {
  much_better: { label: 'Much better', color: 'bg-green-100 text-green-700' },
  slightly_better: { label: 'Slightly better', color: 'bg-green-50 text-green-700' },
  typical: { label: 'Typical day', color: 'bg-gray-100 text-gray-700' },
  slightly_worse: { label: 'Slightly worse', color: 'bg-orange-50 text-orange-700' },
  much_worse: { label: 'Much worse', color: 'bg-red-100 text-red-700' },
};

const ManageTeachersPage = () => {
  const { token } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [activeChild, setActiveChild] = useState(null);
  const [reports, setReports] = useState([]);
  const [sendingEmailFor, setSendingEmailFor] = useState(null);

  // Invite form
  const [teacherEmail, setTeacherEmail] = useState('');
  const [teacherName, setTeacherName] = useState('');
  const [childName, setChildName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newCode, setNewCode] = useState(null);
  const [newInvite, setNewInvite] = useState(null); // {link_id, teacher_email}

  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchInvites = async () => {
    try {
      const res = await axios.get(`${API}/api/teacher/my-invites`, { headers: authHeader });
      setInvites(res.data.invites || []);
    } catch (e) {
      toast.error('Failed to load teacher invites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
    // eslint-disable-next-line
  }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!teacherEmail || !teacherName || !childName) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/teacher/invite`,
        { teacher_email: teacherEmail, teacher_name: teacherName, child_name: childName },
        { headers: authHeader }
      );
      setNewCode(res.data.access_code);
      setNewInvite({ link_id: res.data.link_id, teacher_email: res.data.teacher_email });
      toast.success('Teacher invited! Share the code below.');
      setTeacherEmail('');
      setTeacherName('');
      setChildName('');
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite teacher');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Access code copied!');
  };

  const handleRevoke = async (linkId) => {
    if (!window.confirm('Revoke this teacher\'s access? They will no longer be able to submit reports.')) return;
    try {
      await axios.delete(`${API}/api/teacher/invite/${linkId}`, { headers: authHeader });
      toast.success('Teacher access revoked');
      fetchInvites();
    } catch (e) {
      toast.error('Failed to revoke access');
    }
  };

  const handleSendEmail = async (linkId, teacherEmailAddr) => {
    setSendingEmailFor(linkId);
    try {
      const res = await axios.post(
        `${API}/api/teacher/invite/${linkId}/send-email`,
        {},
        { headers: authHeader }
      );
      toast.success(res.data.message || `Invitation sent to ${teacherEmailAddr}`);
      fetchInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invitation email');
    } finally {
      setSendingEmailFor(null);
    }
  };

  const openReports = async (child) => {
    setActiveChild(child);
    setReportsOpen(true);
    setReportsLoading(true);
    try {
      const res = await axios.get(`${API}/api/teacher/reports/${encodeURIComponent(child)}`, {
        headers: authHeader,
      });
      setReports(res.data.reports || []);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setReportsLoading(false);
    }
  };

  const formatList = (items) => {
    if (!items || items.length === 0) return '—';
    return items
      .map((i) => i.replace(/_/g, ' '))
      .map((i) => i.charAt(0).toUpperCase() + i.slice(1))
      .join(', ');
  };

  // Group invites by child for the reports button
  const childrenWithInvites = Array.from(
    new Set(invites.filter((i) => i.status === 'active').map((i) => i.child_name))
  );

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="manage-teachers-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
            <GraduationCap className="w-8 h-8 text-blue-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-nunito font-bold text-[#2D2D2D] mb-2">
              Teacher Observations
            </h1>
            <p className="text-[#57534E] max-w-2xl">
              Invite your child's teacher to submit daily or weekly behavior check-ins.
              Their observations appear on the Medical Timeline and feed into Smart Correlations,
              giving you a complete picture of school-day symptoms.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => { setInviteOpen(true); setNewCode(null); setNewInvite(null); }}
          className="bg-primary text-white hover:bg-primary/90"
          data-testid="invite-teacher-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Invite a Teacher
        </Button>
        {childrenWithInvites.length > 0 && (
          <Button
            variant="outline"
            onClick={() => openReports(childrenWithInvites[0])}
            data-testid="view-reports-btn"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            View Reports
          </Button>
        )}
      </div>

      {/* Active children with reports buttons */}
      {childrenWithInvites.length > 1 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-[#57534E] mb-3">View reports by child</h3>
          <div className="flex flex-wrap gap-2">
            {childrenWithInvites.map((c) => (
              <Button key={c} variant="outline" size="sm" onClick={() => openReports(c)}>
                <FileText className="w-3.5 h-3.5 mr-1.5" /> {c}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {/* Invites list */}
      <Card className="p-6">
        <h2 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-4">
          Your Teacher Invites
        </h2>
        {invites.length === 0 ? (
          <div className="text-center py-12 text-[#A8A29E]">
            <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No teachers invited yet</p>
            <p className="text-sm mt-1">Tap "Invite a Teacher" to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invites.map((invite) => {
              const status = STATUS_META[invite.status] || STATUS_META.pending;
              const StatusIcon = status.icon;
              return (
                <div
                  key={invite.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors"
                  data-testid={`teacher-invite-${invite.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#2D2D2D]">{invite.teacher_name}</span>
                      <Badge className={`${status.color} border-0`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {status.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#57534E] mt-1 truncate">
                      {invite.teacher_email} · for <strong>{invite.child_name}</strong>
                    </p>
                    {invite.status === 'pending' && (
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <code className="px-2 py-1 bg-amber-50 text-amber-800 rounded font-mono text-sm tracking-wider">
                          {invite.access_code}
                        </code>
                        <button
                          onClick={() => handleCopyCode(invite.access_code)}
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          data-testid={`copy-code-${invite.id}`}
                        >
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                        <button
                          onClick={() => handleSendEmail(invite.id, invite.teacher_email)}
                          disabled={sendingEmailFor === invite.id}
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1 disabled:opacity-50"
                          data-testid={`send-email-${invite.id}`}
                        >
                          {sendingEmailFor === invite.id ? (
                            <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</>
                          ) : (
                            <><Send className="w-3 h-3" /> {invite.last_email_sent_at ? 'Resend Email' : 'Send Email'}</>
                          )}
                        </button>
                        {invite.last_email_sent_at && (
                          <span className="text-xs text-stone-500">
                            · sent {formatLocalDate(invite.last_email_sent_at)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                    {invite.status === 'active' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openReports(invite.child_name)}
                        data-testid={`view-reports-${invite.id}`}
                      >
                        <FileText className="w-3.5 h-3.5 mr-1.5" /> Reports
                      </Button>
                    )}
                    {invite.status !== 'revoked' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevoke(invite.id)}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        data-testid={`revoke-${invite.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Revoke
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* How it works */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <BookOpen className="w-5 h-5" /> How Teacher Observations Work
        </h3>
        <ol className="space-y-2 text-sm text-blue-900 list-decimal list-inside">
          <li>Invite a teacher by entering their name, email, and your child's first name.</li>
          <li>You'll receive a 6-character <strong>access code</strong>. Share it with the teacher securely.</li>
          <li>The teacher visits <code className="bg-white px-1.5 py-0.5 rounded">/teacher/login</code> on this site, creates an account using the code, and submits short daily check-ins.</li>
          <li>Reports appear on your Medical Timeline and feed Smart Correlations so you can spot school-day triggers.</li>
        </ol>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite a Teacher</DialogTitle>
            <DialogDescription>
              Generate a one-time access code your child's teacher can use to join.
            </DialogDescription>
          </DialogHeader>

          {newCode ? (
            <div className="space-y-4 py-2">
              <div className="text-center p-6 bg-green-50 rounded-xl border border-green-200">
                <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
                <p className="text-sm text-green-800 mb-3">Share this access code with the teacher:</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="text-3xl font-mono font-bold tracking-widest text-green-900 bg-white px-4 py-2 rounded-lg shadow-sm">
                    {newCode}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => handleCopyCode(newCode)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-green-700 mt-3">
                  Teacher signs up at <strong>/teacher/login → Join with Code</strong>.
                </p>
              </div>

              {newInvite && (
                <Button
                  onClick={() => handleSendEmail(newInvite.link_id, newInvite.teacher_email)}
                  disabled={sendingEmailFor === newInvite.link_id}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12"
                  data-testid="send-invite-email-btn"
                >
                  {sendingEmailFor === newInvite.link_id ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Send Invite Email to {newInvite.teacher_email}</>
                  )}
                </Button>
              )}
              <p className="text-xs text-stone-500 text-center -mt-2">
                One-click email with the code, a short PANDAS/PANS explanation, and a sign-up link.
              </p>

              <Button variant="outline" className="w-full" onClick={() => { setNewCode(null); setNewInvite(null); setInviteOpen(false); }}>
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <Label htmlFor="child-name">Child's First Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="child-name"
                    value={childName}
                    onChange={(e) => setChildName(e.target.value)}
                    placeholder="e.g., Emma"
                    className="pl-9"
                    required
                    data-testid="invite-child-name"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="teacher-name">Teacher's Name</Label>
                <div className="relative mt-1">
                  <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="teacher-name"
                    value={teacherName}
                    onChange={(e) => setTeacherName(e.target.value)}
                    placeholder="e.g., Mrs. Smith"
                    className="pl-9"
                    required
                    data-testid="invite-teacher-name"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="teacher-email">Teacher's Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="teacher-email"
                    type="email"
                    value={teacherEmail}
                    onChange={(e) => setTeacherEmail(e.target.value)}
                    placeholder="teacher@school.edu"
                    className="pl-9"
                    required
                    data-testid="invite-teacher-email"
                  />
                </div>
                <p className="text-xs text-stone-500 mt-1">
                  This email must match the one the teacher uses when joining.
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white"
                disabled={submitting}
                data-testid="invite-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Access Code'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reports Dialog */}
      <Dialog open={reportsOpen} onOpenChange={setReportsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reports-dialog">
          <DialogHeader>
            <DialogTitle>Teacher Reports — {activeChild}</DialogTitle>
            <DialogDescription>
              Recent observations from teachers (last 30 days).
            </DialogDescription>
          </DialogHeader>
          {reportsLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-10 text-stone-500">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No reports yet for {activeChild}.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map((r) => {
                const rating = RATING_LABELS[r.overall_rating] || RATING_LABELS.typical;
                return (
                  <Card key={r.id} className="p-4" data-testid={`report-${r.id}`}>
                    <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
                      <div>
                        <p className="font-semibold text-[#2D2D2D]">
                          {formatLocalDate(r.report_date, undefined, {
                            weekday: 'short', month: 'short', day: 'numeric',
                          })}
                          <span className="text-xs text-stone-500 ml-2">
                            ({r.report_type === 'weekly' ? 'Weekly' : 'Daily'})
                          </span>
                        </p>
                        <p className="text-xs text-stone-500">By {r.teacher_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${rating.color} border-0`}>{rating.label}</Badge>
                        <Badge variant="outline">Severity {r.severity_score}/10</Badge>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#57534E] mt-3">
                      {r.behavioral_regulation?.length > 0 && (
                        <div><strong>Behavior:</strong> {formatList(r.behavioral_regulation)}</div>
                      )}
                      {r.ocd_behaviors?.length > 0 && r.ocd_behaviors[0] !== 'none' && (
                        <div><strong>OCD:</strong> {formatList(r.ocd_behaviors)}</div>
                      )}
                      {r.attention_learning?.length > 0 && (
                        <div><strong>Attention:</strong> {formatList(r.attention_learning)}</div>
                      )}
                      {r.physical_signs?.length > 0 && r.physical_signs[0] !== 'none' && (
                        <div><strong>Physical:</strong> {formatList(r.physical_signs)}</div>
                      )}
                      {r.triggers?.length > 0 && r.triggers[0] !== 'none' && (
                        <div><strong>Triggers:</strong> {formatList(r.triggers)}</div>
                      )}
                    </div>
                    {r.comment && (
                      <p className="text-sm text-[#2D2D2D] mt-3 p-3 bg-stone-50 rounded-lg italic">
                        "{r.comment}"
                      </p>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageTeachersPage;
